// DEPLOY NOTE (2026-08-02): the changes below were merged in #257 and #258 but
// never reached production - Vercel's daily deployment limit was reached, so
// the automatic deploy on merge silently did not run. The edge functions went
// out normally via GitHub Actions, which is why the assistant's ANSWERS were
// already correct while the RENDERING was not. This comment exists to give
// Vercel a commit to build.
// The assistant slide-out.
//
// Opens IN PLACE so the user keeps their position - that is the entire reason
// this surface exists. "Open full view" promotes to the assistant page and is
// a choice, not the only path.
//
// Results are deliberately summarised here rather than rendered as a full
// table. A drawer is the wrong width for 300 rows, and showing 8 of them
// without saying so is how a user acts on a partial list. Anything that needs
// the whole result, or a bulk action, promotes.

import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { C } from "../../lib/tokens";
import { proApi } from "../../lib/proApi";
import { logAudit } from "../../lib/supabaseClient";
import { useAssistantDrawer } from "./AssistantDrawerContext";

const PURPLE = C.aiFg;
// Themed: this panel was a fixed near-white sheet, so on dark it appeared as a
// full-height white surface with dark chips inside it.
const PURPLE_TINT = "var(--pos-ai-surface)";
const PURPLE_LINE = "var(--pos-ai-line)";

// Up to this many results render as names inside the drawer. Beyond it the
// panel is the wrong shape and the full view is genuinely better.
const INLINE_ROW_LIMIT = 10;

// Results come back as arbitrary columns depending on the question, so the
// label is assembled from whichever identity fields are present rather than
// assuming a fixed shape.
function rowLabel(r, allRows) {
  if (!r || typeof r !== "object") return String(r);
  const name = [r.first_name, r.last_name].filter(Boolean).join(" ")
    || r.patient_name || r.name || r.full_name || null;
  const id = r.mrn || r.plan_member_id || null;

  // On a single-patient chart every row carries the same name, so repeating it
  // is noise on the one fact the user already knows. Only worth showing when
  // the rows are actually about different people.
  const namesVary = !name || !Array.isArray(allRows) || allRows.length < 2
    || allRows.some((x) => {
        const n = [x.first_name, x.last_name].filter(Boolean).join(" ")
          || x.patient_name || x.name || x.full_name || null;
        return n !== name;
      });

  // THE VALUE IS THE ANSWER. A measurement row rendered as
  // "BP diastolic (2026-04-05)" omits the number, which is the entire point of
  // asking. Show it, with units and the change when one is present.
  const val = [r.value_numeric, r.value, r.result_value, r.value_text]
    .find((v) => v !== null && v !== undefined);
  const unit = r.unit || r.units || r.result_unit || "";
  const delta = [r.delta_from_prev, r.change, r.delta]
    .find((v) => typeof v === "number");

  // If the row describes something that HAPPENED to a patient - a work item, a
  // touchpoint, a care gap - lead with that. Eight work items for one patient
  // all rendered as "Linda Chen - MRN-1012": eight identical lines and no
  // answer. The patient name is context, not the subject.
  // test_name must come before any fallback. A lab row has lab_name ("LabCorp")
  // earlier in column order, so the generic first-scalar fallback picked the
  // VENDOR - every result rendered as "LabCorp", which names who ran the test
  // and not what it was.
  // A medication row needs its dose to be useful - "Glucophage" alone does not
  // tell a care manager anything they can act on, and the generic name matters
  // when the brand is unfamiliar.
  const isMed = !!(r.medication_name || r.generic_name);

  // A hospital event has no name field, so it fell through to the first scalar
  // and rendered as its raw HL7 code - "D", "A03". Those mean nothing to a care
  // manager. Built here instead: what kind of visit, where, when, how long.
  const isAdt = !!(r.patient_class || r.source_facility || r.still_admitted !== undefined);
  // A transition episode's first column is a UUID, so rows rendered as raw
  // identifiers. It is described by its state, not its id.
  const isTransition = !!(r.episode_id
    || r.outreach_overdue !== undefined || r.med_rec_required !== undefined);

  const d = (v) => (v ? String(v).slice(0, 10) : null);

  if (isAdt && !isTransition) {
    const kind = r.patient_class === "I" ? "Inpatient stay"
      : r.patient_class === "E" ? "Emergency visit"
      : "Hospital event";
    const when = r.admit_datetime && r.discharge_datetime
      ? d(r.admit_datetime) + " to " + d(r.discharge_datetime)
      : d(r.admit_datetime) || d(r.discharge_datetime) || d(r.event_datetime);
    const bits = [
      r.source_facility || null,
      when,
      typeof r.length_of_stay_days === "number" ? r.length_of_stay_days + " days" : null,
      r.still_admitted ? "still admitted" : null,
      r.discharge_disposition ? "discharged " + r.discharge_disposition : null,
    ].filter(Boolean);
    return kind + (bits.length ? " - " + bits.join(", ") : "");
  }

  if (isTransition) {
    const status = r.status ? String(r.status).replace(/_/g, " ") : "transition";
    const bits = [
      r.event_reference_month ? "for " + d(r.event_reference_month) : null,
      r.outreach_overdue ? "outreach overdue"
        : (r.outreach_done ? "outreach done" : null),
      r.med_rec_required && r.med_rec_completed === false ? "med rec outstanding" : null,
      r.signed_off ? "signed off" : null,
      r.assigned_to_name ? "assigned to " + r.assigned_to_name
        : (r.assigned_to_name === null && r.status === "open" ? "unassigned" : null),
    ].filter(Boolean);
    return "Transition (" + status + ")" + (bits.length ? " - " + bits.join(", ") : "");
  }
  // A care gap is identified by what the measure IS, not its HEDIS abbreviation.
  // sem_care_gaps carries measure_code and measure_description but no plain
  // "measure", so rows fell through to the first scalar and rendered as bare
  // codes - "CBP", "HBD", "WCV". Those are unreadable to anyone who does not
  // already know the measure set, which is most people.
  const gapName = r.measure_description
    ? r.measure_description + (r.measure_code ? " (" + r.measure_code + ")" : "")
    : (r.measure || r.measure_code || null);
  const subject = r.title || r.test_name || r.item_type || gapName || r.metric_name
    || (isMed
        ? (r.medication_name || r.generic_name)
          + (r.generic_name && r.medication_name && r.generic_name !== r.medication_name
              ? " (" + r.generic_name + ")" : "")
          + (r.dose ? " " + r.dose : "")
        : null)
    || r.priority_population || r.contact_method || r.appt_type || r.reason_label
    || r.code || null;
  if (subject) {
    const valuePart = (val !== undefined && val !== null)
      ? " " + val + (unit ? " " + unit : "")
        + (typeof delta === "number" && delta !== 0
            ? " (" + (delta > 0 ? "up " : "down ") + Math.abs(delta) + ")" : "")
      : "";
    const detail = [
      r.is_critical ? "critical" : (r.is_abnormal ? "abnormal" : null),
      r.is_unreviewed ? "unreviewed" : null,
      // Whether a contact SUCCEEDED is the point of a touchpoint row, and who
      // made it was added to the views specifically so it could be shown.
      typeof r.successful_contact === "boolean"
        ? (r.successful_contact ? "reached" : "not reached") : null,
      r.delivered_by_name || null,
      r.counts_toward_tcm_contact === true ? "counts for billing" : null,
      // A gap without its year and state is not actionable - the same measure
      // can be open for 2026 and closed for 2025.
      r.measurement_year ? String(r.measurement_year) : null,
      typeof r.gap_open === "boolean"
        ? (r.gap_open ? "open" : (r.closed_at ? "closed " + String(r.closed_at).slice(0, 10) : "closed"))
        : null,
      r.submeasure && r.submeasure !== r.measure_code ? r.submeasure : null,
      // An appointment without its status is just a visit type - the status is
      // usually why it was asked about.
      r.appt_date ? String(r.appt_date).slice(0, 10) : null,
      r.provider_name || null,
      // Frequency and indication are what make a med list readable. Status is
      // skipped for meds - every row in an active-medication answer says
      // "Active", which is noise on every line.
      isMed ? (r.frequency || null) : null,
      isMed ? (r.indication || null) : null,
      isMed ? null : (r.severity || r.status || r.plan_status || null),
      r.due_at ? "due " + String(r.due_at).slice(0, 10)
        : r.measured_at ? String(r.measured_at).slice(0, 10)
        : r.touchpoint_at ? String(r.touchpoint_at).slice(0, 10)
        : r.resulted_at ? String(r.resulted_at).slice(0, 10) : null,
    ].filter(Boolean).join(", ");
    const who = (name && namesVary) ? " - " + name : "";
    return String(subject) + valuePart + (detail ? " (" + detail + ")" : "") + who;
  }

  if (name && id) return name + " - " + id;
  if (name) return name;
  // No identity columns: show the first scalar so the row is not blank.
  // Skip fields that identify a SOURCE or a record rather than the thing
  // itself - naming the vendor is how every lab result came out as "LabCorp".
  const SKIP = ["patient_id", "enrollment_id", "item_id", "id", "lab_name",
                "source", "source_detail", "recall_source", "payer_name",
                // Identifiers and raw interface codes. A row rendered as a UUID
                // or an HL7 trigger tells the reader nothing.
                "episode_id", "adt_event_id", "care_plan_id", "assertion_id",
                "appointment_id", "encounter_id", "event_type", "adt_trigger",
                "mapped_adt_reason_code", "adt_reason_code"];
  const k = Object.keys(r).find((key) => {
    const v = r[key];
    return (typeof v === "string" || typeof v === "number") && SKIP.indexOf(key) === -1;
  });
  return k ? String(r[k]) : "(record)";
}

// An aggregate question - "how many", "what is the average" - returns ONE ROW
// holding the figure. Rendering the ROW COUNT for those said "1 result" when
// the answer was 5, and did it for every count and average asked. The number
// is the entire reply; the row is just its container.
//
// Detected structurally rather than by guessing from the question: a single
// row whose columns are all scalars and which carries no patient identity is
// an aggregate, not a record.
// A single row of facts about one thing - "is this member at billing risk?",
// "are they unable to reach?", "what are their priority populations?" - is
// rendered as LABELLED FIELDS, not squeezed through rowLabel.
//
// rowLabel exists to name a record in a list. Given a status row it has no
// subject to find, so it fell back to whichever scalar came first: a billing
// risk answer showed the acuity tier, a UTR answer showed an attempt count,
// and priority populations showed nothing useful at all. Every value was real
// and none of them was the answer.
//
// The old 4-key cap was the reason these missed this path. Widened, and arrays
// are now allowed - priority_populations is a text[] and was disqualifying the
// whole row.
function aggregateFields(rows) {
  if (!Array.isArray(rows) || rows.length !== 1) return null;
  const r = rows[0];
  if (!r || typeof r !== "object") return null;
  const keys = Object.keys(r).filter((k) => k !== "patient_id" && k !== "enrollment_id");
  if (keys.length === 0 || keys.length > 10) return null;
  // A row carrying a person's name is a RECORD, not a set of facts - let
  // rowLabel handle it so the name leads.
  if (keys.some((k) => ["first_name", "last_name", "mrn", "patient_name"].indexOf(k) !== -1)) return null;
  const ok = keys.every((k) => {
    const v = r[k];
    return v === null || typeof v === "number" || typeof v === "string"
      || typeof v === "boolean" || Array.isArray(v);
  });
  if (!ok) return null;
  return keys.map((k) => ({
    label: k.replace(/_/g, " "),
    value: fmtFieldValue(r[k]),
  }));
}

// Booleans and arrays are the two shapes that made these answers unreadable.
// "true" is not an answer to "are they unable to reach?", and an empty array
// rendered as nothing at all.
function fmtFieldValue(v) {
  if (v === null || v === undefined) return "-";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (Array.isArray(v)) return v.length ? v.join(", ") : "none";
  return String(v);
}

// Kept for pins: the single headline figure when there is exactly one.
function primaryValue(rows) {
  const fields = aggregateFields(rows);
  return fields && fields.length === 1 ? fields[0].value : null;
}

// Results render IN the panel. Making someone leave the workspace to see who
// is on a list defeats the point of answering in place, so the full set is
// always reachable here: first 10, then expand.
// Results are COLLAPSED by default now that the answer leads.
//
// The synthesis usually says everything a care manager needs, and a wall of
// rows underneath it buries the answer they just got. The detail is one click
// away for the times they want to check the working or see who is in the list.
//
// `open` starts true only when there is no answer above to stand on its own -
// then the rows ARE the answer and hiding them would leave an empty reply.
function ResultList({ rows, hasAnswerAbove }) {
  const [open, setOpen] = useState(!hasAnswerAbove);
  const [expanded, setExpanded] = useState(false);
  if (!Array.isArray(rows) || rows.length === 0) return null;
  const shown = expanded ? rows : rows.slice(0, INLINE_ROW_LIMIT);
  const hidden = rows.length - shown.length;
  const noun = rows.length === 1 ? "result" : "results";
  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        style={{ marginTop: 6, fontSize: 11, color: PURPLE, background: "transparent",
                 border: "0.5px solid " + PURPLE_LINE, borderRadius: 6,
                 padding: "3px 9px", cursor: "pointer", fontFamily: "inherit" }}
      >
        {"Show the " + rows.length + " " + noun}
      </button>
    );
  }
  return (
    <>
      <button
        type="button"
        onClick={() => { setOpen(false); setExpanded(false); }}
        style={{ marginTop: 6, fontSize: 10, color: C.textTertiary, background: "transparent",
                 border: "none", padding: 0, cursor: "pointer", fontFamily: "inherit" }}
      >
        {"Hide " + noun}
      </button>
      <ul style={{ margin: "5px 0 0", padding: "0 0 0 14px", listStyle: "disc" }}>
        {shown.map((r, ri) => (
          <li key={ri} style={{ fontSize: 11, lineHeight: 1.6, color: C.textPrimary }}>
            {rowLabel(r, rows)}
          </li>
        ))}
      </ul>
      {rows.length > INLINE_ROW_LIMIT && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          style={{ marginTop: 5, fontSize: 10, color: PURPLE, background: "transparent",
                   border: "none", padding: 0, cursor: "pointer", fontFamily: "inherit" }}
        >
          {expanded ? "Show fewer" : "Show all " + rows.length}
          {!expanded && hidden > 0 ? " (" + hidden + " more)" : ""}
        </button>
      )}
    </>
  );
}

// Copy carries the QUESTION, ITS SCOPE, the answer and a timestamp - never the
// bare answer.
//
// The scope line is the point. An answer pasted into a care plan or an email
// without it is a number with no provenance, which is how a working figure
// quietly becomes a reported one. The SOP warns against that; this format
// enforces it instead of relying on the user to remember.
function buildCopyText({ question, scope, answerText, rowCount, rows, agg }) {
  const lines = [];
  lines.push("Q: " + (question || "(question unavailable)"));
  lines.push("Scope: " + (scope
    ? scope.label + (typeof scope.matched_count === "number"
        ? " (" + scope.matched_count + " records)" : "")
    : "Whole practice - no page scope applied"));

  // THE RESULTS ARE THE POINT. Copy used to carry the explanation and a count
  // and stop there - so pasting an answer gave a description of the query and
  // none of what it found. The explanation says HOW the number was reached;
  // the rows say WHAT it was.
  if (Array.isArray(agg) && agg.length > 0) {
    lines.push("Answer: " + agg.map((f) => f.value + " " + f.label).join(", "));
  } else if (typeof rowCount === "number" && rowCount > 0) {
    lines.push("Answer: " + rowCount + (rowCount === 1 ? " record" : " records"));
  }

  if (Array.isArray(rows) && rows.length > 0) {
    lines.push("");
    // Everything, not the first ten. The panel truncates because it is 320px
    // wide; a clipboard has no such constraint, and a partial paste is how a
    // list quietly becomes wrong somewhere else.
    for (const r of rows) lines.push("  - " + rowLabel(r, rows));
    lines.push("");
  }

  if (answerText) lines.push("How this was found: " + answerText);
  lines.push("PracticeOS AI Assistant - " + new Date().toLocaleString());
  return lines.join("\n");
}

function CopyButton({ payload }) {
  const [done, setDone] = useState(false);
  const copy = async () => {
    const text = buildCopyText(payload);
    // The clipboard is a PHI egress path exactly like a download: the copied
    // rows carry patient names and values. Log it (metadata and ids only, never
    // the copied text).
    const rows = Array.isArray(payload && payload.rows) ? payload.rows : [];
    const ids = Array.from(new Set(rows.map((r) => r && r.patient_id).filter(Boolean)));
    logAudit({
      action: "Export",
      entityType: "patients",
      entityId: ids.length === 1 ? ids[0] : null,
      patientId: ids.length === 1 ? ids[0] : null,
      details: { document_kind: "assistant_answer", format: "clipboard", row_count: rows.length, patient_ids: ids.slice(0, 500) },
    }).catch(() => {});
    try {
      await navigator.clipboard.writeText(text);
      setDone(true);
      setTimeout(() => setDone(false), 1800);
    } catch (e) {
      console.error("[assistant] copy failed", e);
    }
  };
  return (
    <button
      type="button" onClick={copy}
      style={{ fontSize: 10, borderRadius: 6, padding: "4px 9px", fontFamily: "inherit",
               border: "0.5px solid " + C.borderMid, background: "transparent",
               color: C.textPrimary, cursor: "pointer" }}
    >{done ? "Copied" : "Copy"}</button>
  );
}

export default function AssistantDrawer() {
  const drawer = useAssistantDrawer();
  const navigate = useNavigate();
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  // Open by default. A user arriving at a new surface cannot guess what it can
  // answer, and a guide they have to find first is a guide most people never
  // see. It collapses once they ask something, and the header toggle brings it
  // back at any time.
  const [showGuide, setShowGuide] = useState(true);
  // On a narrow screen the drawer overlays rather than compresses, so nothing
  // should be inset - otherwise panels would shrink for room that was never
  // given up.
  const [isNarrow, setIsNarrow] = useState(() =>
    typeof window !== "undefined" && window.matchMedia("(max-width: 720px)").matches);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 720px)");
    const onChange = (e) => setIsNarrow(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  const [err, setErr] = useState(null);
  const endRef = useRef(null);

  const open = drawer && drawer.open;
  const scope = drawer && drawer.scope;
  const thread = (drawer && drawer.thread) || [];
  const pins = (drawer && drawer.pins) || [];

  // A prefilled question (command bar "Ask the assistant") lands in the ask
  // box once and is then cleared from the shared state. It is not sent.
  const draft = drawer && drawer.draft;
  useEffect(() => {
    if (!open || draft == null) return;
    setText(draft);
    drawer.clearDraft();
    // drawer is rebuilt on every provider render; keyed on the draft itself.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, draft]);

  useEffect(() => {
    if (open && endRef.current) endRef.current.scrollIntoView({ block: "end" });
  }, [open, thread.length, busy]);

  // A new scope is a new universe of questions, so re-open the guide even
  // mid-session. Keyed on region_id rather than the scope object, which is
  // rebuilt on every render.
  const scopeRegionId = scope ? scope.region_id : null;
  useEffect(() => {
    if (scopeRegionId) setShowGuide(true);
  }, [scopeRegionId]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") drawer.closeDrawer(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, drawer]);

  // THE RIGHT-EDGE CONTRACT.
  //
  // 13 panels in this app are position:fixed against the right edge - handoffs,
  // member quick view, copilot review, chart synthesis, toasts, several
  // worklists. They are positioned against the VIEWPORT, so the assistant
  // drawer sits on top of them and cuts them in half.
  //
  // Rather than patch 13 files and every future one, the drawer publishes how
  // much room it is taking as a CSS variable. Any right-edge panel reads it:
  //
  //   right: "var(--assistant-inset, 0px)"
  //
  // One source of truth, no imports, and it degrades to 0px wherever the drawer
  // is not mounted - so a panel using the token is always correct.
  //
  // Set on documentElement rather than a wrapper because fixed panels often
  // render through a portal and are not inside the drawer's subtree.
  useEffect(() => {
    const root = document.documentElement;
    const inset = (open && !isNarrow) ? "320px" : "0px";
    root.style.setProperty("--assistant-inset", inset);
    return () => { root.style.setProperty("--assistant-inset", "0px"); };
  }, [open, isNarrow]);

  if (!drawer || !open) return null;

  const send = async () => {
    const q = text.trim();
    if (!q || busy) return;
    setBusy(true);
    setErr(null);
    // The scope is captured with the question so the thread records where it
    // was asked from, even after the pill is cleared or the user navigates.
    const asked = { role: "user", text: q, scope: scope, path: drawer.currentPath };
    drawer.setThread((t) => t.concat([asked]));
    setText("");
    try {
      const res = await proApi.assistantQuery({
        query: q,
        conversationId: drawer.conversationId,
        scope: scope,
      });
      if (res && res.conversationId) drawer.setConversationId(res.conversationId);
      const rows = res && Array.isArray(res.results) ? res.results : [];
      drawer.setThread((t) => t.concat([{
        role: "assistant",
        // The ANSWER leads. The query explanation is kept underneath as
        // provenance - useful for checking a threshold or an exclusion, but
        // not the thing the user asked for.
        text: (res && (res.synthesis || res.explanation || res.content)) || "No answer returned.",
        method: res && res.synthesis ? (res.explanation || null) : null,
        rowCount: rows.length,
        // Small result sets render in the panel. Making someone leave the page
        // to see 3 names defeats the point of answering in place.
        rows: rows.length > 0 ? rows : null,
        // Aggregates are computed from the raw rows, not the truncated set, so
        // a count can never be lost to a display limit.
        agg: aggregateFields(rows),
        // The edge function returns `runError` for a query that failed to build
        // or execute. Reading only `error` meant those rendered as a plain
        // explanation with no sign anything went wrong - the user saw a
        // confident sentence and an empty result.
        error: (res && (res.runError || res.error)) || null,
      }]));
    } catch (e) {
      const msg = (e && e.message) || String(e);
      setErr(msg);
      drawer.setThread((t) => t.concat([{ role: "assistant", text: null, error: msg }]));
    } finally {
      setBusy(false);
    }
  };

  const promote = () => {
    drawer.closeDrawer();
    // Carry the conversation, not just the scope. Promoting used to open a
    // blank assistant page, which discarded the thread the user had been
    // building - the opposite of what "open full view" promises.
    navigate("/pro/assistant", {
      state: { assistantScope: scope, assistantConversationId: drawer.conversationId },
    });
  };

  return (
    <aside
      role="complementary"
      aria-label="AI Assistant"
      style={{
        width: 320, flex: "0 0 320px", height: "100vh",
        // Phone: cover the screen. In the flex row a 320px drawer left the
        // page about 70px wide, unreadable and untappable behind it.
        ...(isNarrow ? { position: "fixed", inset: 0, width: "100%", height: "100dvh", borderLeft: "none" } : {}),
        borderLeft: "0.5px solid " + PURPLE_LINE, background: PURPLE_TINT,
        display: "flex", flexDirection: "column",
        // position:relative is required for zIndex to apply at all - the
        // previous zIndex:120 sat on a statically positioned element and did
        // nothing, so Flow Mode's full-viewport overlay (fixed, inset 0,
        // z-index 100000) covered the drawer completely. It opened; it was
        // simply invisible.
        //
        // relative keeps the drawer in the flex row, so a normal page still
        // COMPRESSES rather than being covered. Above 100000 so it also layers
        // over Flow Mode, which is the one surface that takes the whole
        // viewport. Modals in this app sit at 5000-6000, well below.
        ...(isNarrow ? {} : { position: "relative" }), zIndex: 100010,
      }}
    >
      <div style={{
        padding: "10px 12px", borderBottom: "0.5px solid " + PURPLE_LINE,
        display: "flex", alignItems: "flex-start", gap: 7, flexShrink: 0,
      }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={PURPLE}
             strokeWidth="1.8" aria-hidden="true">
          <path d="M12 3l2 5 5 2-5 2-2 5-2-5-5-2 5-2z" />
        </svg>
        {/* Wraps rather than truncating. "Reading Remaining in this s..." told
            the user neither the full scope nor that anything was hidden - a
            silent ellipsis on the one label that says what the answer covers. */}
        <span style={{ fontSize: 12, color: "var(--pos-ai-fg)", minWidth: 0, lineHeight: 1.35,
                       overflowWrap: "anywhere" }}>
          {scope ? <>Reading <b style={{ fontWeight: 600 }}>{scope.label}</b></> : "Ask about your practice"}
        </span>
        {scope && Array.isArray(scope.examples) && scope.examples.length > 0 && (
          <button
            type="button"
            onClick={() => setShowGuide((v) => !v)}
            aria-label={showGuide ? "Hide example questions" : "Show example questions"}
            aria-pressed={showGuide}
            title="What can I ask here?"
            style={{
              marginLeft: "auto", border: "0.5px solid " + PURPLE_LINE,
              background: showGuide ? C.aiBg : "transparent",
              color: PURPLE, borderRadius: 6, padding: "2px 7px",
              fontSize: 10, fontFamily: "inherit", cursor: "pointer", lineHeight: 1.4,
            }}
          >What can I ask?</button>
        )}
        <button
          type="button" onClick={drawer.closeDrawer} aria-label="Close assistant"
          style={{ marginLeft: 6, border: "none", background: "transparent", cursor: "pointer",
                   color: C.textTertiary, fontSize: 16, lineHeight: 1, fontFamily: "inherit" }}
        >{"\u00d7"}</button>
      </div>

      <div style={{ flex: 1, overflowY: "auto", padding: "11px 12px", display: "flex", flexDirection: "column", gap: 9 }}>
        {pins.length > 0 && (
          <>
            <div style={{ fontSize: 9, textTransform: "uppercase", letterSpacing: "0.09em",
                          color: C.textTertiary, fontWeight: 600 }}>Pinned</div>
            {pins.map((pin) => {
              // Staleness is COMPUTED, not guessed: the pin snapshotted what
              // its scope matched, so a change is detectable. It is marked,
              // never silently invalidated and never silently refreshed.
              const stale = pin.scope && scope
                && pin.scope.region_id === scope.region_id
                && typeof pin.matchedAtPin === "number"
                && typeof scope.matched_count === "number"
                && pin.matchedAtPin !== scope.matched_count;
              return (
                <div key={pin.id} style={{ background: C.bgPrimary, border: "0.5px solid " + C.borderLight,
                       borderLeft: "2px solid " + PURPLE, padding: "7px 9px" }}>
                  <div style={{ display: "flex", alignItems: "flex-start", gap: 6 }}>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <p style={{ margin: 0, fontSize: 10, color: C.textTertiary }}>
                        {pin.scope ? pin.scope.label : "Whole practice"}
                      </p>
                      {pin.value && (
                        <p style={{ margin: "2px 0 0", fontSize: 18, fontWeight: 700,
                                    color: C.textPrimary, lineHeight: 1.1 }}>
                          {pin.value}
                          {pin.valueLabel && (
                            <span style={{ fontSize: 10, fontWeight: 400, color: C.textTertiary }}>
                              {" " + pin.valueLabel}
                            </span>
                          )}
                        </p>
                      )}
                      <p style={{ margin: "3px 0 0", fontSize: 11, color: C.textSecondary, lineHeight: 1.45 }}>
                        {pin.answerText}
                      </p>
                      {pin.rows && <ResultList rows={pin.rows} hasAnswerAbove />}
                      {stale && (
                        <p style={{ margin: "3px 0 0", fontSize: 9, color: C.warnFg }}>
                          filters changed since pinned
                        </p>
                      )}
                    </div>
                    <button
                      type="button" onClick={() => drawer.removePin(pin.id)}
                      aria-label="Remove pin"
                      style={{ border: "none", background: "transparent", cursor: "pointer",
                               color: C.textTertiary, fontSize: 13, lineHeight: 1, fontFamily: "inherit" }}
                    >{"\u00d7"}</button>
                  </div>
                  <div style={{ marginTop: 5 }}>
                    <CopyButton payload={{ question: pin.question, scope: pin.scope,
                                           answerText: pin.answerText, rowCount: pin.rowCount,
                                           rows: pin.rows }} />
                  </div>
                </div>
              );
            })}
            <div style={{ fontSize: 9, textTransform: "uppercase", letterSpacing: "0.09em",
                          color: C.textTertiary, fontWeight: 600, marginTop: 2 }}>Now</div>
          </>
        )}
        {thread.length === 0 && (
          <p style={{ margin: 0, fontSize: 12, color: C.textTertiary, lineHeight: 1.55 }}>
            {scope
              ? "Ask about " + scope.label + ". Remove the tag below to ask across the whole practice."
              : "Ask a question about your practice. Hover a card or list and choose ask to scope it."}
          </p>
        )}

        {/* What can I ask here? The list is declared BY THE REGION, so it can
            never list a question the region cannot answer. Clicking one fills
            the composer rather than only demonstrating it.
            Shown by default on an empty thread, and reachable at any time from
            the header - a user mid-conversation is MORE likely to wonder what
            else they can ask, not less. */}
        {(showGuide || thread.length === 0) && scope && Array.isArray(scope.examples) && scope.examples.length > 0 && (
          <div>
            <p style={{ margin: "0 0 5px", fontSize: 9, textTransform: "uppercase",
                        letterSpacing: "0.09em", color: C.textTertiary, fontWeight: 600 }}>
              Everything you can ask here
              {(() => {
                const ex = scope.examples;
                const n = (Array.isArray(ex) && ex.length && typeof ex[0] === "object" && ex[0].group)
                  ? ex.reduce((a, g) => a + ((g.items || []).length), 0)
                  : (ex || []).length;
                return n ? " (" + n + ")" : "";
              })()}
            </p>
            {/* The full set, grouped and scrollable. Capped in height so a
                comprehensive list never buries the conversation below it. */}
            <div style={{ display: "flex", flexDirection: "column", gap: 8,
                          maxHeight: 320, overflowY: "auto", paddingRight: 2 }}>
              {(Array.isArray(scope.examples) && scope.examples.length > 0
                && typeof scope.examples[0] === "object" && scope.examples[0].group
                  ? scope.examples
                  : [{ group: null, items: scope.examples }]
              ).map((section, gi) => (
                <div key={gi}>
                  {section.group && (
                    <div style={{ fontSize: 9, textTransform: "uppercase",
                                  letterSpacing: "0.08em", color: C.textTertiary,
                                  fontWeight: 600, margin: "0 0 4px" }}>
                      {section.group}
                    </div>
                  )}
                  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    {(section.items || []).map((ex, i) => (
                      <button
                        key={i} type="button"
                        onClick={() => { setText(ex); setShowGuide(false); }}
                        style={{ textAlign: "left", fontSize: 11, lineHeight: 1.4,
                                 padding: "5px 8px", borderRadius: 6, cursor: "pointer",
                                 border: "0.5px solid " + C.borderLight, background: C.bgPrimary,
                                 color: C.textPrimary, fontFamily: "inherit" }}
                      >{ex}</button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {thread.map((m, i) => (
          m.role === "user" ? (
            <div key={i} style={{ alignSelf: "flex-end", maxWidth: "88%" }}>
              {m.scope && (
                <div style={{ fontSize: 10, color: C.textTertiary, textAlign: "right", marginBottom: 2 }}>
                  {m.scope.label}
                </div>
              )}
              <div style={{ background: C.teal, color: C.onAccent, fontSize: 12, padding: "5px 9px", borderRadius: 8 }}>
                {m.text}
              </div>
            </div>
          ) : (
            <div key={i}>
              {m.error ? (
                <p style={{ margin: 0, fontSize: 12, color: C.dangerSolid, lineHeight: 1.5 }}>{m.error}</p>
              ) : (
                <>
                  <p style={{ margin: 0, fontSize: 12, color: C.textPrimary, lineHeight: 1.5 }}>{m.text}</p>
                  {m.method && (
                    <p style={{ margin: "4px 0 0", fontSize: 10, color: C.textTertiary, lineHeight: 1.45 }}>
                      {m.method}
                    </p>
                  )}
                </>
              )}
              {!m.error && m.text && (
                <div style={{ display: "flex", gap: 5, marginTop: 6, flexWrap: "wrap" }}>
                  <button
                    type="button"
                    onClick={() => drawer.addPin({
                      question: (thread[i - 1] && thread[i - 1].text) || "",
                      scope: (thread[i - 1] && thread[i - 1].scope) || null,
                      answerText: m.text,
                      rowCount: m.rowCount,
                      // The figure is the point of the answer. Pinning the
                      // sentence without it said what was counted but not what
                      // it came to.
                      value: primaryValue(m.rows)
                        || (m.agg ? m.agg.map((f) => f.value + " " + f.label).join(", ") : null)
                        || (m.rowCount ? String(m.rowCount) : null),
                      valueLabel: (primaryValue(m.rows) || m.agg) ? null
                        : (m.rowCount === 1 ? "result" : "results"),
                      rows: m.rows || null,
                    })}
                    style={{ fontSize: 10, borderRadius: 6, padding: "4px 9px", fontFamily: "inherit",
                             border: "0.5px solid " + PURPLE, background: PURPLE, color: C.onAccent,
                             cursor: "pointer" }}
                  >Pin this</button>
                  <CopyButton payload={{
                    question: (thread[i - 1] && thread[i - 1].text) || "",
                    scope: (thread[i - 1] && thread[i - 1].scope) || null,
                    answerText: m.text, rowCount: m.rowCount,
                    rows: m.rows, agg: m.agg,
                  }} />
                </div>
              )}
              {typeof m.rowCount === "number" && m.rowCount > 0 && (
                <div style={{ marginTop: 6 }}>
                  <div style={{
                    background: C.bgPrimary, border: "0.5px solid " + C.borderLight,
                    borderRadius: 8, padding: "6px 9px", fontSize: 11, color: C.textPrimary,
                  }}>
                    {m.agg ? (
                      <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
                        {m.agg.map((f, fi) => (
                          <div key={fi}>
                            <div style={{ fontSize: 20, fontWeight: 700, lineHeight: 1.1,
                                          color: C.textPrimary }}>{f.value}</div>
                            {m.agg.length > 1 && (
                              <div style={{ fontSize: 10, color: C.textTertiary,
                                            textTransform: "capitalize" }}>{f.label}</div>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div style={{ fontWeight: 600 }}>
                        {m.rowCount} {m.rowCount === 1 ? "result" : "results"}
                      </div>
                    )}
                    {m.rows && <ResultList rows={m.rows} hasAnswerAbove={!!m.method} />}
                  </div>
                  {/* Honest about the limit rather than quietly truncating. */}
                  <p style={{ margin: "5px 0 0", fontSize: 10, color: C.textTertiary }}>
                    {m.agg
                      ? "Ask for the names to see who these are."
                      : "Export and outreach need the full view."}
                  </p>
                  <button
                    type="button" onClick={promote}
                    style={{ marginTop: 6, fontSize: 10, borderRadius: 6, padding: "4px 9px",
                             border: "0.5px solid " + PURPLE, background: PURPLE, color: C.onAccent,
                             cursor: "pointer", fontFamily: "inherit" }}
                  >Open full view</button>
                </div>
              )}
            </div>
          )
        ))}
        {busy && <p style={{ margin: 0, fontSize: 11, color: C.textTertiary }}>Thinking...</p>}
        {err && !busy && <p style={{ margin: 0, fontSize: 11, color: C.dangerSolid }}>{err}</p>}
        <div ref={endRef} />
      </div>

      <div style={{ padding: "9px 12px", borderTop: "0.5px solid " + PURPLE_LINE, background: C.bgPrimary }}>
        {scope && (
          <div style={{ marginBottom: 6 }}>
            <span style={{
              display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11,
              color: "var(--pos-ai-fg)", background: C.aiBg, borderRadius: 20, padding: "2px 4px 2px 9px",
            }}>
              {scope.label}
              {typeof scope.matched_count === "number" && (
                <span style={{ color: "var(--pos-ai-muted)" }}>{"\u00b7 " + scope.matched_count}</span>
              )}
              <button
                type="button" onClick={drawer.clearScope}
                aria-label={"Remove scope: " + scope.label}
                style={{ width: 14, height: 14, borderRadius: "50%", border: "none",
                         background: "var(--pos-ai-bg)", color: "var(--pos-ai-fg)", fontSize: 9,
                         lineHeight: 1, cursor: "pointer", fontFamily: "inherit",
                         display: "inline-flex", alignItems: "center", justifyContent: "center" }}
              >{"\u00d7"}</button>
            </span>
          </div>
        )}
        <div style={{ display: "flex", gap: 6, alignItems: "flex-end" }}>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder={scope ? "Ask about this" : "Ask about your practice"}
            rows={2}
            disabled={busy}
            style={{ flex: 1, padding: "6px 8px", border: "0.5px solid " + C.borderMid,
                     borderRadius: 6, fontSize: 12, fontFamily: "inherit", resize: "none",
                     outline: "none", color: C.textPrimary }}
          />
          <button
            type="button" onClick={send} disabled={busy || !text.trim()}
            style={{ fontSize: 11, borderRadius: 6, padding: "6px 10px", fontFamily: "inherit",
                     border: "0.5px solid " + (text.trim() ? C.teal : C.borderMid),
                     background: text.trim() ? C.teal : "transparent",
                     color: text.trim() ? C.onAccent : C.textTertiary,
                     cursor: text.trim() && !busy ? "pointer" : "default" }}
          >Ask</button>
        </div>
      </div>
    </aside>
  );
}
